import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { defaultMedicalInsuranceTypesOptions } from '@nxsys/constants/AppConstants';
import { MedicalInsuranceTypeEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

export type MedicalInsuranceFormValues = {
  value: string;
  startDate: string;
  type: Option<string> | null;
  description: string;
  endDate: string;
  // NX-5652: one-off premiums are charged in a single tax period rather than
  // apportioned across a date range, so only one of taxPeriod / start+end
  // applies. Without this field a one-off saved from here had no taxPeriod and
  // was silently never charged.
  taxPeriod: number | null;
  eeContribution: string;
  totalEeContribution: string;
};

export type MedicalInsuranceFormRef = {
  submit: () => void;
  setFieldsValue: (values: Partial<MedicalInsuranceFormValues>) => void;
};

type MedicalInsuranceFormProps = {
  formRef?: Ref<MedicalInsuranceFormRef>;
  onSubmit?: (values: MedicalInsuranceFormValues) => void;
  defaultValues?: MedicalInsuranceFormValues;
};

const MedicalInsuranceForm: React.FC<MedicalInsuranceFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<MedicalInsuranceFormValues>();

  // AppSelect stores either the raw enum or an { value } option depending on
  // whether the value came from the API or from the user picking one.
  const selectedType = Form.useWatch('type', form);
  const isOneOff =
    (typeof selectedType === 'string'
      ? selectedType
      : (selectedType as Option<string> | null)?.value) ===
    MedicalInsuranceTypeEnum.ONE_OFF;

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    setFieldsValue(values) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish = (values: MedicalInsuranceFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.type']}
        name="type"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultMedicalInsuranceTypesOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.grossPremium']}
        name="value"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      {isOneOff ? (
        <Form.Item
          label={messages['common.taxPeriod']}
          name="taxPeriod"
          rules={[
            {
              required: true,
              message: messages['form.pleaseSelect'],
            },
          ]}
        >
          <TaxWeekMonthSelect
            placeholder={messages['form.select']}
            onFilter={(value) => {
              form.setFieldsValue({ taxPeriod: value.taxPeriod });
            }}
          />
        </Form.Item>
      ) : (
        <>
          <Form.Item
            label={messages['common.startDate']}
            name="startDate"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
            />
          </Form.Item>
          <Form.Item label={messages['common.endDate']} name="endDate">
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
            />
          </Form.Item>
        </>
      )}
      <Form.Item
        label={messages['common.employeeContribution']}
        name="eeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.totalEmployeeContribution']}
        name="totalEeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
          disabled
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default MedicalInsuranceForm;
